import { Stack, Typography } from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import { useTranslation } from 'react-i18next';

import { useExpenseFormBlurredValuesStore } from '../../stores/expenseFormBlurredValues';
import { ExpenseFormType } from '../../types';
import { type CurrencyCodeSelectOption } from '@/api/typesense/currencyCodes';
import { EmployeeSelectOption } from '@/api/typesense/employees';
import { type PaymentMethodSelectOption } from '@/api/typesense/paymentMethods';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { parseLocaleNumber } from '@/utils';

interface ExpenseSummaryHeaderProps {
  expenseCurrency: CurrencyCodeSelectOption;
  paymentMethod?: PaymentMethodSelectOption;
  supplier?: string;
  category?: string;
  selectedEmployee?: EmployeeSelectOption;
  expenseType: ExpenseFormType;
}

export const ExpenseSummaryHeader = ({
  expenseCurrency,
  paymentMethod,
  supplier,
  category,
  selectedEmployee,
  expenseType,
}: ExpenseSummaryHeaderProps) => {
  const { t } = useTranslation('expenses');
  const { expenseDetailValues } = useExpenseFormBlurredValuesStore();

  const expenseAmountCurrencyIsoCode = expenseCurrency?.isoCode;
  const reimbursementCurrencyIsoCode =
    selectedEmployee?.legalEntityCurrencyIsoCode;

  const formattedAmount = useFormatCurrency(
    parseLocaleNumber(expenseDetailValues.amountIncludingVat),
    {
      currency: expenseAmountCurrencyIsoCode,
    },
  );

  const formattedReimbursementAmount = useFormatCurrency(
    parseLocaleNumber(expenseDetailValues.targetCurrencyConvertedAmount),
    {
      currency: reimbursementCurrencyIsoCode,
    },
  );

  const currentPaymentMethodIsReimbursable = paymentMethod
    ? paymentMethod.isReimbursable
    : false;

  // this should probably be handled in a better when we can access the initials from Typense directly
  const employeeInitials = selectedEmployee
    ? selectedEmployee?.value?.split(' ')[0]
    : null;

  return (
    <Stack
      direction="column"
      gap={1}
      alignItems="flex-end"
      justifyContent="flex-start"
    >
      <Stack
        direction="row"
        gap={2}
        justifyItems="flex-end"
        alignItems="center"
      >
        <Typography variant="bodyRegular" component="span">
          {`${t('summaryHeader.Amount')}: ${!expenseDetailValues.amountIncludingVat || isNaN(parseLocaleNumber(expenseDetailValues.amountIncludingVat)) ? '-' : formattedAmount}`}
        </Typography>
        {expenseType === 'personal' && (
          <Typography variant="bodyRegular" component="span">
            {`${t('summaryHeader.Reimbursement')}: ${!currentPaymentMethodIsReimbursable || !expenseDetailValues.targetCurrencyConvertedAmount || isNaN(parseLocaleNumber(expenseDetailValues.targetCurrencyConvertedAmount)) ? '-' : formattedReimbursementAmount}`}
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        gap={2}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Typography
          variant="label"
          color={scssVariables.ColorBlack60}
          component="span"
        >
          {`${t('summaryHeader.Supplier')}: ${supplier || '-'}`}
        </Typography>
        <Typography
          variant="label"
          color={scssVariables.ColorBlack60}
          component="span"
        >
          {`${t('summaryHeader.Category')}: ${category || '-'}`}
        </Typography>
        {expenseType === 'personal' && (
          <Typography
            variant="label"
            color={scssVariables.ColorBlack60}
            component="span"
          >
            {`${t('summaryHeader.Employee')}: ${employeeInitials || '-'}`}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
